import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  Typography,
  Switch,
  Tabs,
  Tab,
  Slider,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { IResultLayer } from '../../core/interfaces/result.interface';
import { ChartPattern } from '../../core/enums/chart-pattern.enum';
import { MuiColorInput } from 'mui-color-input';

export interface DetailsConfigurationProps {
  layer: IResultLayer;
  onLayerEdit: (layer: IResultLayer) => void;
}

export const DetailsConfiguration: React.FC<DetailsConfigurationProps> = ({ layer, onLayerEdit }) => {
  const [layerData, setLayerData] = useState<IResultLayer>(layer);
  const [selectedClassIndex, setSelectedClassIndex] = useState<number>(0);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setLayerData(layer);
  }, [layer]);

  const classCategories = useMemo(() => {
    return layerData.details.classes.map((c) => c.category).join(',');
  }, [layerData.details.classes]);

  useEffect(() => {
    setSelectedClassIndex(0);
  }, [layer.resultId, layerData.details.classes.length, classCategories]);

  const debouncedOnLayerEdit = useCallback(
    (updatedLayer: IResultLayer) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        onLayerEdit(updatedLayer);
      }, 300); // 300ms debounce
    },
    [onLayerEdit],
  );

  const updateLayerDataDebounced = useCallback(
    (updatedLayer: IResultLayer) => {
      setLayerData(updatedLayer);
      debouncedOnLayerEdit(updatedLayer);
    },
    [debouncedOnLayerEdit],
  );

  const updateLayerData = useCallback(
    (updatedLayer: IResultLayer) => {
      setLayerData(updatedLayer);
      onLayerEdit(updatedLayer);
    },
    [onLayerEdit],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box display="flex" flexDirection="column" sx={{ width: '100%' }}>
      <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
        Layer Configuration
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <label htmlFor="layers-panel">Display in the Layers Panel</label>
        <Switch
          id="layers-panel"
          checked={layerData.details.visible}
          onChange={(e) => {
            layerData.details.visible = e.target.checked;
            updateLayerData({ ...layerData });
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <label htmlFor="default-enabled">Default Enabled</label>
        <Switch
          id="default-enabled"
          checked={layerData.details.defaultEnabled !== false}
          onChange={(e) => {
            layerData.details.defaultEnabled = e.target.checked;
            updateLayerData({ ...layerData });
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', width: '100%', height: '720px', mt: 2 }}>
        <Tabs
          value={selectedClassIndex}
          orientation="vertical"
          onChange={(_, idx) => {
            setSelectedClassIndex(idx);
          }}
          aria-label="class tabs"
          variant="scrollable"
          sx={{ borderRight: 1, borderColor: 'divider' }}
        >
          {layerData.details.classes.map((classItem) => (
            <Tab key={classItem.category} label={`${classItem.displayName}`} sx={{ fontSize: '10px' }} />
          ))}
        </Tabs>

        {layerData.details.classes.map((classItem, layerClassIndex) => {
          if (layerClassIndex === selectedClassIndex) {
            return (
              <Box
                key={`box_class_${classItem.category}`}
                display={layerClassIndex === selectedClassIndex ? 'flex' : 'none'}
                justifyContent="space-evenly"
                alignContent="flex-start"
                flexDirection="column"
                sx={{ ml: 2 }}
              >
                <TextField
                  label="Display Name"
                  value={classItem.displayName || ''}
                  fullWidth
                  onChange={(e) => {
                    const updatedClasses = [...layerData.details.classes];
                    const classIndex = updatedClasses.findIndex((c) => c.category === classItem.category);

                    if (classIndex !== -1) {
                      const updatedClass = {
                        ...updatedClasses[classIndex],
                        displayName: e.target.value,
                      };

                      updatedClasses[classIndex] = updatedClass;
                      const updatedLayer = {
                        ...layerData,
                        details: {
                          ...layerData.details,
                          classes: updatedClasses,
                        },
                      };

                      updateLayerDataDebounced(updatedLayer);
                    }
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label htmlFor="defaultEnabled">Default Enabled</label>
                  <Switch
                    checked={classItem.defaultEnabled !== false}
                    onChange={(e) => {
                      const updatedClasses = [...layerData.details.classes];
                      const classIndex = updatedClasses.findIndex((c) => c.category === classItem.category);
                      if (classIndex !== -1) {
                        updatedClasses[classIndex] = {
                          ...updatedClasses[classIndex],
                          defaultEnabled: e.target.checked,
                        };
                        updateLayerData({
                          ...layerData,
                          details: {
                            ...layerData.details,
                            classes: updatedClasses,
                          },
                        });
                      }
                    }}
                  />
                </Box>
                <MuiColorInput
                  name="fillColor"
                  value={classItem.fillColor}
                  isAlphaHidden={true}
                  format="hex"
                  onChange={(color) => {
                    const updatedClasses = [...layerData.details.classes];
                    const classIndex = updatedClasses.findIndex((c) => c.category === classItem.category);
                    if (classIndex !== -1) {
                      updatedClasses[classIndex] = {
                        ...updatedClasses[classIndex],
                        fillColor: color,
                      };
                      updateLayerDataDebounced({
                        ...layerData,
                        details: {
                          ...layerData.details,
                          classes: updatedClasses,
                        },
                      });
                    }
                  }}
                  label="Fill Color"
                />

                <Box>
                  <Typography gutterBottom sx={{ fontSize: '12px' }}>
                    Fill Transparency ({classItem.fillTransparency})
                  </Typography>
                  <Slider
                    value={classItem.fillTransparency}
                    onChange={(e, value) => {
                      const updatedClasses = [...layerData.details.classes];
                      const classIndex = updatedClasses.findIndex((c) => c.category === classItem.category);
                      if (classIndex !== -1) {
                        updatedClasses[classIndex] = {
                          ...updatedClasses[classIndex],
                          fillTransparency: value as number,
                        };
                        updateLayerDataDebounced({
                          ...layerData,
                          details: {
                            ...layerData.details,
                            classes: updatedClasses,
                          },
                        });
                      }
                    }}
                    step={0.1}
                    min={0}
                    max={1}
                    valueLabelDisplay="auto"
                  />
                </Box>

                <FormControl fullWidth>
                  <InputLabel id={`fill-pattern-select-label-${layerClassIndex}`}>Fill Pattern</InputLabel>
                  <Select
                    labelId={`fill-pattern-select-label-${layerClassIndex}`}
                    id={`fill-pattern-select-${layerClassIndex}`}
                    value={classItem.fillPattern}
                    label="Fill Pattern"
                    onChange={(e) => {
                      const updatedClasses = [...layerData.details.classes];
                      const classIndex = updatedClasses.findIndex((c) => c.category === classItem.category);
                      if (classIndex !== -1) {
                        updatedClasses[classIndex] = {
                          ...updatedClasses[classIndex],
                          fillPattern: e.target.value as ChartPattern,
                        };
                        updateLayerDataDebounced({
                          ...layerData,
                          details: {
                            ...layerData.details,
                            classes: updatedClasses,
                          },
                        });
                      }
                    }}
                  >
                    {Object.keys(ChartPattern).map((key) => (
                      <MenuItem key={key} value={ChartPattern[key]}>
                        {ChartPattern[key]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <MuiColorInput
                  name="edgeColor"
                  value={classItem.edgeColor}
                  isAlphaHidden={true}
                  format="hex"
                  fullWidth
                  onChange={(color) => {
                    const updatedClasses = [...layerData.details.classes];
                    const classIndex = updatedClasses.findIndex((c) => c.category === classItem.category);
                    if (classIndex !== -1) {
                      updatedClasses[classIndex] = {
                        ...updatedClasses[classIndex],
                        edgeColor: color,
                      };
                      updateLayerDataDebounced({
                        ...layerData,
                        details: {
                          ...layerData.details,
                          classes: updatedClasses,
                        },
                      });
                    }
                  }}
                  label="Edge Color"
                />

                <Box>
                  <Typography gutterBottom sx={{ fontSize: '12px' }}>
                    Edge Transparency ({classItem.edgeTransparency})
                  </Typography>
                  <Slider
                    value={classItem.edgeTransparency}
                    onChange={(e, value) => {
                      const updatedClasses = [...layerData.details.classes];
                      const classIndex = updatedClasses.findIndex((c) => c.category === classItem.category);
                      if (classIndex !== -1) {
                        updatedClasses[classIndex] = {
                          ...updatedClasses[classIndex],
                          edgeTransparency: value as number,
                        };
                        updateLayerDataDebounced({
                          ...layerData,
                          details: {
                            ...layerData.details,
                            classes: updatedClasses,
                          },
                        });
                      }
                    }}
                    step={0.1}
                    min={0}
                    max={1}
                    valueLabelDisplay="auto"
                  />
                </Box>

                <Box>
                  <Typography gutterBottom sx={{ fontSize: '12px' }}>
                    Edge Thickness ({classItem.edgeThickness})
                  </Typography>
                  <Slider
                    value={classItem.edgeThickness}
                    onChange={(e, value) => {
                      const updatedClasses = [...layerData.details.classes];
                      const classIndex = updatedClasses.findIndex((c) => c.category === classItem.category);
                      if (classIndex !== -1) {
                        updatedClasses[classIndex] = {
                          ...updatedClasses[classIndex],
                          edgeThickness: value as number,
                        };
                        updateLayerDataDebounced({
                          ...layerData,
                          details: {
                            ...layerData.details,
                            classes: updatedClasses,
                          },
                        });
                      }
                    }}
                    step={1}
                    min={0}
                    max={5}
                    valueLabelDisplay="auto"
                  />
                </Box>

                {classItem.infoValues.map((column, i) => (
                  <Box
                    key={`box_infoValues_${classItem.category}`}
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                  >
                    <TextField
                      label={layerData.details.infoColumns[i].displayName}
                      value={column}
                      fullWidth
                      onChange={(e) => {
                        const updatedClasses = [...layerData.details.classes];
                        const classIndex = updatedClasses.findIndex((c) => c.category === classItem.category);
                        if (classIndex !== -1) {
                          const updatedInfoValues = [...updatedClasses[classIndex].infoValues];
                          updatedInfoValues[i] = e.target.value;
                          updatedClasses[classIndex] = {
                            ...updatedClasses[classIndex],
                            infoValues: updatedInfoValues,
                          };
                          updateLayerDataDebounced({
                            ...layerData,
                            details: {
                              ...layerData.details,
                              classes: updatedClasses,
                            },
                          });
                        }
                      }}
                    />
                  </Box>
                ))}
              </Box>
            );
          }
        })}
      </Box>
    </Box>
  );
};
